import { useEffect } from "react";
import { useLocation } from "wouter";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { AppShell } from "@/components/layout/app-shell";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { PlusCircle, FileText, BarChart, Zap, Ruler, Users, Building2, ArrowRight } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useCustomer } from "@/hooks/use-customer";
import { MeasurementsExample } from "@/components/examples/measurements-example";
import { VisionUploader } from "@/components/vision-uploader";

export default function HomePage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [, navigate] = useLocation();
  const { activeCustomerId, setActiveCustomerId, customers, isLoading } = useCustomer();

  const queryKey = activeCustomerId ? `/api/analyses?customerId=${activeCustomerId}` : "/api/analyses?all=true";

  // Fetch recent analyses
  const { data: recentAnalyses = [] } = useQuery<any[]>({
    queryKey: [queryKey],
    queryFn: async ({ queryKey }) => {
      const url = queryKey[0] as string;
      const res = await fetch(url, {
        credentials: "include",
      });
      if (!res.ok) return [];
      return await res.json();
    },
  });

  // Get top 3 most recent analyses
  const topAnalyses = recentAnalyses.slice(0, 3);

  // If Admin comes here without a customer, boot them to the customer list
  useEffect(() => {
    if (user?.role === "Admin" && !activeCustomerId) {
      navigate("/");
    }
  }, [user, activeCustomerId, navigate]);

  if (user?.role === "Admin" && !activeCustomerId) return null;

  // Find the selected customer's name if we are impersonating
  const activeCustomerObj = customers?.find(c => c.id.toString() === activeCustomerId);

  return (
    <AppShell>
      {/* Hero Section */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">
          {t("home.welcome")}, {activeCustomerObj ? activeCustomerObj.username : user?.username}!
        </h1>
        <p className="text-muted-foreground">
          {activeCustomerObj ? `You are currently viewing data for ${activeCustomerObj.username}.` : t("home.subtitle")}
        </p>
      </div>

      {/* Quick Actions (Vision / Upload) */}
      <div className="mb-8">
        <Card className="border-primary/20 bg-primary/5">
          <CardHeader>
            <CardTitle>Automatic Equipment Capture</CardTitle>
            <CardDescription>
              Use AI to scan an equipment nameplate to start a new Pump or ECM analysis instantly. 
            </CardDescription>
          </CardHeader>
          <CardContent>
            <VisionUploader 
              onAnalysisComplete={(data) => {
                sessionStorage.setItem("visionExtractedData", JSON.stringify(data));
                navigate("/analysis/new");
              }}
            />
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">{t("home.newAnalysis")}</CardTitle>
            <CardDescription>{t("home.newAnalysisDesc")}</CardDescription>
          </CardHeader>
          <CardFooter>
            <Button 
              className="w-full" 
              onClick={() => navigate("/analysis/new")}
            >
              <PlusCircle className="mr-2 h-4 w-4" />
              {t("home.startNew")}
            </Button>
          </CardFooter>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">{t("home.savedAnalyses")}</CardTitle>
            <CardDescription>{t("home.savedAnalysesDesc")}</CardDescription>
          </CardHeader>
          <CardFooter>
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => navigate("/analyses")}
            >
              <FileText className="mr-2 h-4 w-4" />
              {t("home.viewSaved")}
            </Button>
          </CardFooter>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">{t("home.profile")}</CardTitle>
            <CardDescription>{t("home.profileDesc")}</CardDescription>
          </CardHeader>
          <CardFooter>
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => navigate("/profile")}
            >
              <BarChart className="mr-2 h-4 w-4" />
              {t("home.manageProfile")}
            </Button>
          </CardFooter>
        </Card>
      </div>
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">{t("home.recentAnalyses")}</h2>
          {recentAnalyses.length > 3 && (
            <Button 
              variant="link" 
              onClick={() => navigate("/analyses")}
            >
              {t("home.viewAll")}
            </Button>
          )}
        </div>

        {topAnalyses.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {topAnalyses.map((analysis) => (
              <Card key={analysis.id} className="overflow-hidden">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">{analysis.manufacturer} {analysis.model}</CardTitle>
                  <CardDescription>
                    {new Date(analysis.createdAt).toLocaleDateString()} - {analysis.status}
                  </CardDescription>
                </CardHeader>
                <CardContent className="pb-2">
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <p className="text-muted-foreground">{t("form.application")}</p>
                      <p>{analysis.application}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">{t("form.location")}</p>
                      <p>{analysis.location}</p>
                    </div>
                  </div>
                </CardContent>
                <CardFooter>
                  <Button 
                    variant="outline" 
                    className="w-full" 
                    onClick={() => navigate(`/analysis/${analysis.id}`)}
                  >
                    <Zap className="mr-2 h-4 w-4" />
                    {analysis.status === "completed" ? t("home.viewResults") : t("home.continueAnalysis")}
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 border rounded-lg bg-muted/30">
            <p className="text-muted-foreground">
              {t("home.noAnalyses")}
            </p>
            <Button 
              variant="link"
              onClick={() => navigate("/analysis/new")}
              className="mt-2"
            >
              {t("home.createFirst")}
            </Button>
          </div>
        )}
      </div>

      {/* Unit Measurement Example */}
      <div className="mb-8">
        <Card>
          <CardHeader className="flex flex-row items-center gap-2">
            <Ruler className="h-6 w-6 text-primary" />
            <CardTitle>{t("examples.measurementUnits")}</CardTitle>
          </CardHeader>
          <CardContent>
            <MeasurementsExample />
          </CardContent>
        </Card>
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>{t("home.whatIsEcm.title")}</CardTitle>
          </CardHeader>
          <CardContent>
            <p>{t("home.whatIsEcm.desc1")}</p>
            <p className="mt-2">{t("home.whatIsEcm.desc2")}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t("home.benefitsTitle")}</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              <li className="flex items-start">
                <svg className="h-5 w-5 text-primary mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
                <span>{t("home.benefits.energy")}</span>
              </li>
              <li className="flex items-start">
                <svg className="h-5 w-5 text-primary mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
                <span>{t("home.benefits.costs")}</span>
              </li>
              <li className="flex items-start">
                <svg className="h-5 w-5 text-primary mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
                <span>{t("home.benefits.maintenance")}</span>
              </li>
              <li className="flex items-start">
                <svg className="h-5 w-5 text-primary mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
                <span>{t("home.benefits.carbon")}</span>
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
}
